import { FC, useState } from 'react';
import { message, Tabs } from 'antd';
import { DollarCircleOutlined, GiftOutlined, ApiOutlined, ClusterOutlined } from '@ant-design/icons';
import { Card } from '../../../../../components/card/card';
import { API } from '../../../../../apis';
import { postViaAuth, responseValidator } from '../../../../../apis/api';
import {
    KucoinCredentialsSettings,
    PerfectMoneySettings,
    Tron24Settings,
    UtopiaSettings,
} from '../../../../../core/domain';
import { SettingsSection, TextInput } from '../shared/settings-ui';
import { useLoadSettings } from '../shared/use-settings';
import { useGoogleAuth } from '../shared/use-google-auth';
import '../settings.scss';

/* ---------------- پرفکت‌مانی ---------------- */
const PerfectMoneyTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<PerfectMoneySettings>(
        API.siteSettings.perfectMoneySettings,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changePerfectMoneyConfigs, {
            accountId: data?.accountId ?? '',
            payeeAccount: data?.payeeAccount ?? '',
            password: data?.password ?? '',
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('تنظیمات پرفکت‌مانی ذخیره شد.');
    };

    return (
        <SettingsSection title="پرفکت‌مانی" description="اطلاعات حساب سرویس پرفکت‌مانی." onSave={save} loading={saving}>
            <TextInput label="Account ID" value={data?.accountId} onChange={(v) => setData({ ...data, accountId: v } as PerfectMoneySettings)} />
            <TextInput label="Payee Account" value={data?.payeeAccount} onChange={(v) => setData({ ...data, payeeAccount: v } as PerfectMoneySettings)} />
            <TextInput label="Password" htmlType="password" value={data?.password} onChange={(v) => setData({ ...data, password: v } as PerfectMoneySettings)} />
        </SettingsSection>
    );
};

/* ---------------- یو‌ووچر (یوتوپیا) ---------------- */
const UtopiaTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<UtopiaSettings>(API.siteSettings.uVoucherSetting, setCardLoading);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changeUVoucherSetting, {
            token: data?.token ?? '',
            baseUrl: data?.baseUrl ?? '',
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('تنظیمات یو‌ووچر ذخیره شد.');
    };

    return (
        <SettingsSection title="یو‌ووچر (یوتوپیا)" description="اتصال به سرویس یو‌ووچر." onSave={save} loading={saving}>
            <TextInput label="Token" value={data?.token} onChange={(v) => setData({ ...data, token: v } as UtopiaSettings)} />
            <TextInput label="Base URL" value={data?.baseUrl} onChange={(v) => setData({ ...data, baseUrl: v } as UtopiaSettings)} />
        </SettingsSection>
    );
};

/* ---------------- کوکوین ---------------- */
const KucoinTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<KucoinCredentialsSettings>(
        API.siteSettings.kucoinCredentials,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.changeKucoinCredentials, {
            apiKey: data?.apiKey ?? '',
            apiSecret: data?.apiSecret ?? '',
            apiPassPhrase: data?.apiPassPhrase ?? '',
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('اطلاعات کوکوین ذخیره شد.');
    };

    return (
        <SettingsSection title="کوکوین" description="کلیدهای دسترسی حساب صرافی کوکوین." onSave={save} loading={saving}>
            <TextInput label="API Key" value={data?.apiKey} onChange={(v) => setData({ ...data, apiKey: v } as KucoinCredentialsSettings)} />
            <TextInput label="API Secret" htmlType="password" value={data?.apiSecret} onChange={(v) => setData({ ...data, apiSecret: v } as KucoinCredentialsSettings)} />
            <TextInput label="API Passphrase" htmlType="password" value={data?.apiPassPhrase} onChange={(v) => setData({ ...data, apiPassPhrase: v } as KucoinCredentialsSettings)} />
        </SettingsSection>
    );
};

/* ---------------- ترون۲۴ (نیازمند تایید دو مرحله‌ای) ---------------- */
const Tron24Tab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<Tron24Settings>(API.siteSettings.tron24Setting, setCardLoading);
    const { requestAuth, modal } = useGoogleAuth();

    const save = () => {
        requestAuth(async (code) => {
            const res: any = await postViaAuth(API.siteSettings.changeTron24Setting, {
                apiKey: data?.apiKey ?? '',
                secretKey: data?.secretKey ?? '',
                hotWalletAddress: data?.hotWalletAddress ?? '',
                commissionWalletAddress: data?.commissionWalletAddress ?? '',
                googleAuthCode: code,
            });
            if (responseValidator(res.status)) {
                message.success('تنظیمات ترون۲۴ ذخیره شد.');
                return true;
            }
            return false;
        });
    };

    return (
        <>
            <SettingsSection
                title="ترون۲۴"
                description="کلیدها و آدرس کیف‌پول‌های سرویس ترون۲۴. ذخیره تغییرات نیازمند تایید دو مرحله‌ای است."
                onSave={save}
                saveText="ذخیره و تایید"
            >
                <TextInput label="API Key" value={data?.apiKey} onChange={(v) => setData({ ...data, apiKey: v } as Tron24Settings)} />
                <TextInput label="Secret Key" htmlType="password" value={data?.secretKey} onChange={(v) => setData({ ...data, secretKey: v } as Tron24Settings)} />
                <TextInput label="آدرس کیف‌پول داغ (Hot Wallet)" value={data?.hotWalletAddress} onChange={(v) => setData({ ...data, hotWalletAddress: v } as Tron24Settings)} />
                <TextInput label="آدرس کیف‌پول کارمزد" value={data?.commissionWalletAddress} onChange={(v) => setData({ ...data, commissionWalletAddress: v } as Tron24Settings)} />
            </SettingsSection>
            {modal}
        </>
    );
};

export const IntegrationsSettings: FC = () => {
    const [cardLoading, setCardLoading] = useState(false);

    const items = [
        { key: 'perfect-money', label: 'پرفکت‌مانی', icon: <DollarCircleOutlined />, children: <PerfectMoneyTab setCardLoading={setCardLoading} /> },
        { key: 'utopia', label: 'یو‌ووچر', icon: <GiftOutlined />, children: <UtopiaTab setCardLoading={setCardLoading} /> },
        { key: 'kucoin', label: 'کوکوین', icon: <ApiOutlined />, children: <KucoinTab setCardLoading={setCardLoading} /> },
        { key: 'tron24', label: 'ترون۲۴', icon: <ClusterOutlined />, children: <Tron24Tab setCardLoading={setCardLoading} /> },
    ];

    return (
        <Card blur={cardLoading} className="pmx-settings-page">
            <Tabs items={items} destroyInactiveTabPane />
        </Card>
    );
};
